// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditTransportReferenceProperty } from '@/audit/api/types';
import { AuditCarrierLinkValue } from '@/audit/components/audit-carrier-link-value';
import { AuditPropertyUpdateLabel } from '@/audit/components/audit-property-update-label';
import { AuditPropertyValue } from '@/audit/components/audit-property-value';
import { toAuditCarrierName } from '@/audit/utils/to-audit-carrier-name';
import { ArrowForwardRounded } from '@/constants/hardfin-standard-icons';
import { Stack, Typography } from '@mui/material';

interface Props {
  property: AuditTransportReferenceProperty;
}

/** Renders an order's expected transport as its carrier and transport reference. */
export function AuditTransportReferenceLabel(props: Props) {
  const isSnapshot = props.property.oldValue === undefined;

  // A row whose value is absent on both sides states nothing, so it is dropped
  // rather than rendered as "None → None".
  const oldCarrier = toAuditCarrierName(props.property.oldValue);
  const newCarrier = toAuditCarrierName(props.property.newValue);
  const oldReference = props.property.oldValue?.transportReference ?? null;
  const newReference = props.property.newValue?.transportReference ?? null;
  const showCarrier = oldCarrier !== null || newCarrier !== null;
  const showReference = oldReference !== null || newReference !== null;

  if (!showCarrier && !showReference) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {showCarrier && (
        <Typography variant="caption" component="div" marginLeft={2}>
          {isSnapshot ? (
            <>
              Carrier:&nbsp;
              <AuditPropertyValue value={newCarrier} />
            </>
          ) : (
            <AuditPropertyUpdateLabel
              name="Carrier"
              oldValue={oldCarrier}
              newValue={newCarrier}
            />
          )}
        </Typography>
      )}
      {showReference && (
        <Typography
          variant="caption"
          component="div"
          marginLeft={2}
          display="flex"
          alignItems="center"
          flexWrap="wrap"
        >
          Transport reference:&nbsp;
          {!isSnapshot && (
            <>
              <AuditCarrierLinkValue
                carrier={props.property.oldValue?.carrier}
                identifier={oldReference}
              />
              <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 0.5 }} />
            </>
          )}
          <AuditCarrierLinkValue
            carrier={props.property.newValue?.carrier}
            identifier={newReference}
          />
        </Typography>
      )}
    </Stack>
  );
}
